// Pick latest stable SemVer tag from a list — used by `avatar sync` để chọn
// pack version mới nhất một cách độc lập với pack team.
//
// Background:
//   Trước v1.7.1 Avatar dùng `git tags` rồi `tags[length-1]` → alphabetical sort.
//   Hoạt động OK với v0.1 → v0.4, NHƯNG break silently khi:
//     - v0.10.0 < v0.4.0 (alphabetical)
//     - v1.0.0 < v0.9.0 (alphabetical)
//     - Pre-release v0.5.0-rc1 lẫn vào (user không muốn pick auto)
//
//   v1.7.1 fix: parse SemVer chuẩn, sort numeric, default SKIP pre-release.
//   User muốn pre-release → pass `--version v0.5.0-rc1` explicit.
//
// Decision (Q1=SemVer sort, Q2=skip pre-release):
//   - Parse `vMAJOR.MINOR.PATCH` (optional 'v' prefix, optional `-PRERELEASE`)
//   - Tags không match SemVer pattern → skip (vd "release-1", "2026-05-29")
//   - Default skip pre-release (có dấu `-` sau PATCH)
//   - Sort numeric tăng dần [major, minor, patch] → return tag cuối

// Strict SemVer regex: optional v prefix, 3 số, optional -PRERELEASE.
// Pre-release matches: -rc1, -beta.2, -alpha, ...
const SEMVER_REGEX = /^v?(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/;

interface ParsedSemVer {
  raw: string; // Original tag string (vd "v0.4.0")
  major: number;
  minor: number;
  patch: number;
  prerelease: string | null; // null cho stable, string cho rc/beta/alpha
}

// Parse 1 tag string. Trả null nếu không match SemVer.
export function parseSemVerTag(tag: string): ParsedSemVer | null {
  const match = tag.match(SEMVER_REGEX);
  if (!match) return null;
  const [, major, minor, patch, prerelease] = match;
  return {
    raw: tag,
    major: Number.parseInt(major ?? "0", 10),
    minor: Number.parseInt(minor ?? "0", 10),
    patch: Number.parseInt(patch ?? "0", 10),
    prerelease: prerelease ?? null,
  };
}

// Pick latest stable tag từ array. Mặc định skip pre-release.
// includePrerelease=true → cho phép pre-release (vd CI test bleeding edge).
//
// Trả null nếu KHÔNG có tag SemVer nào (caller xử lý: error hoặc fallback).
export function pickLatestStableSemVerTag(
  tags: string[],
  includePrerelease = false,
): string | null {
  const parsed = tags
    .map(parseSemVerTag)
    .filter((t): t is ParsedSemVer => t !== null)
    .filter((t) => includePrerelease || t.prerelease === null);

  if (parsed.length === 0) return null;

  // Sort tăng dần: [major, minor, patch] numeric.
  parsed.sort((a, b) => {
    if (a.major !== b.major) return a.major - b.major;
    if (a.minor !== b.minor) return a.minor - b.minor;
    return a.patch - b.patch;
  });

  // Last element = latest.
  return parsed[parsed.length - 1]?.raw ?? null;
}
